import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Button } from 'src/@/components/ui/button';
import { LoadingButton } from 'src/@/components/ui/loading-button';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Separator } from 'src/@/components/ui/separator';
import { Switch } from 'src/@/components/ui/switch';

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from 'src/@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from 'src/@/components/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from 'src/@/components/ui/command';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/@/components/ui/tooltip';
import { cn } from 'src/@/lib/utils';
import Iconify from 'src/components/Iconify';
import Editor from './editor';
import { UploadChamados } from 'src/components/upload';
import getActionListaUsuariosByClienteId from 'src/actions/usuarios/getActionListaUsuariosByClienteId';
import getActionListaSetoresByClienteId from 'src/actions/clientes/getActionListaSetoresByClienteId';
import { actionListaCaixasEntrada } from 'src/actions/chamados/actionChamadosConfig';
import { Check, ChevronsUpDown, CalendarIcon, Plus, Trash2 } from 'lucide-react';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar } from 'src/@/components/ui/calendar';

// ----------------------------------------------------------------------
// DateTimePicker
// ----------------------------------------------------------------------
function DateTimePicker({ value, onChange }) {
    const [date, setDate] = useState(value || new Date());
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (value) setDate(value);
    }, [value]);

    const handleDateSelect = (selectedDate) => {
        if (selectedDate) {
            const newDate = new Date(selectedDate);
            newDate.setHours(date.getHours());
            newDate.setMinutes(date.getMinutes());
            setDate(newDate);
            onChange?.(newDate);
        }
    };

    const handleTimeChange = (type, val) => {
        const newDate = new Date(date);
        if (type === 'hour') newDate.setHours(parseInt(val));
        else if (type === 'minute') newDate.setMinutes(parseInt(val));
        setDate(newDate);
        onChange?.(newDate);
    };

    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn(
                        'w-full justify-start text-left font-normal rounded-xl border-gray-200 dark:border-[#383838] hover:bg-gray-50 dark:hover:bg-[#383838]/50',
                        !date && 'text-muted-foreground'
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4 text-gray-400 dark:text-[#ABABAD]" />
                    {date ? format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR }) : <span>Selecione data e hora</span>}
                </Button>
            </PopoverTrigger>
            <PopoverContent onOpenAutoFocus={(e) => e.preventDefault()} className="w-auto p-0">
                <div className="flex">
                    <Calendar locale={ptBR} mode="single" selected={date} onSelect={handleDateSelect} initialFocus />
                    <div className="flex flex-col border-l p-2">
                        <ScrollArea className="h-[200px] w-20">
                            <div className="flex flex-col">
                                {hours.map((hour) => (
                                    <Button key={hour} size="sm" variant={date && date.getHours() === hour ? 'default' : 'ghost'} className="h-8 w-full" onClick={() => handleTimeChange('hour', hour.toString())}>
                                        {hour.toString().padStart(2, '0')}
                                    </Button>
                                ))}
                            </div>
                        </ScrollArea>
                        <ScrollArea className="h-[200px] w-20 border-t">
                            <div className="flex flex-col">
                                {minutes.map((minute) => (
                                    <Button key={minute} size="sm" variant={date && date.getMinutes() === minute ? 'default' : 'ghost'} className="h-8 w-full" onClick={() => handleTimeChange('minute', minute.toString())}>
                                        {minute.toString().padStart(2, '0')}
                                    </Button>
                                ))}
                            </div>
                        </ScrollArea>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}

// ----------------------------------------------------------------------
// Combobox
// ----------------------------------------------------------------------
function Combobox({ options = [], value, onChange, getOptionLabel, renderOption, renderValue, placeholder, className, multiple = false, ...props }) {
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const filteredOptions = options.filter((option) =>
        getOptionLabel(option).toLowerCase().includes(searchValue.toLowerCase())
    );

    const handleSelect = (option) => {
        if (multiple) {
            const currentValues = Array.isArray(value) ? value : [];
            const isSelected = currentValues.some((v) => getOptionLabel(v) === getOptionLabel(option));
            if (isSelected) {
                onChange(currentValues.filter((v) => getOptionLabel(v) !== getOptionLabel(option)));
            } else {
                onChange([...currentValues, option]);
            }
        } else {
            onChange?.(option);
            setOpen(false);
        }
    };

    const displayValue = multiple
        ? value && value.length > 0
            ? `${value.length} selecionado(s)`
            : placeholder || 'Selecionar'
        : value
            ? (renderValue ? renderValue(value) : getOptionLabel(value))
            : placeholder || 'Selecionar';

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn('w-full justify-between rounded-xl border-gray-200 dark:border-[#383838] hover:bg-gray-50 dark:hover:bg-[#383838]/50', className)}
                    {...props}
                >
                    {displayValue}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[10000]" align="start">
                <Command>
                    <CommandInput placeholder="Pesquisar..." value={searchValue} onValueChange={setSearchValue} />
                    <CommandList>
                        <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
                        <CommandGroup>
                            {filteredOptions.map((option, index) => {
                                const isSelected = multiple
                                    ? value && value.some((v) => getOptionLabel(v) === getOptionLabel(option))
                                    : value && getOptionLabel(value) === getOptionLabel(option);
                                return (
                                    <CommandItem key={index} onSelect={() => handleSelect(option)} className="cursor-pointer">
                                        <Check className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                                        {renderOption ? renderOption(option) : getOptionLabel(option)}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

// ----------------------------------------------------------------------
// FieldLabel (label com tooltip e indicador de obrigatório)
// ----------------------------------------------------------------------
function FieldLabel({ children, tooltip, required }) {
    return (
        <div className="flex items-center gap-1.5">
            <Label className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider">
                {children}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {tooltip && (
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="cursor-help text-gray-300 dark:text-[#565856] hover:text-gray-500 dark:hover:text-[#ABABAD] transition-colors">
                                <Iconify icon="mdi:information-outline" width={14} />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[240px] text-xs">
                            {tooltip}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
}

// ----------------------------------------------------------------------
// TIPO CONFIG
// ----------------------------------------------------------------------
const TIPO_CONFIG = {
    chamado_interno: {
        label: 'Interno',
        desc: 'Entre usuarios da plataforma',
        icon: 'mdi:account-group',
        bg: 'bg-blue-50 dark:bg-blue-500/10',
        bgSelected: 'bg-blue-100 dark:bg-blue-500/20 border-blue-400 dark:border-blue-500/50 ring-2 ring-blue-200 dark:ring-blue-500/30',
        iconColor: 'text-blue-600 dark:text-blue-400',
        iconBg: 'bg-white dark:bg-[#222529]',
        iconBgInactive: 'bg-white/60 dark:bg-[#222529]/60',
        checkBg: 'bg-white dark:bg-[#222529]',
        bannerBg: 'bg-blue-50 dark:bg-blue-500/10 border-b border-blue-100 dark:border-blue-500/20',
    },
    chamado_externo: {
        label: 'Externo',
        desc: 'De publico externo para a plataforma',
        icon: 'mdi:earth',
        bg: 'bg-orange-50 dark:bg-orange-500/10',
        bgSelected: 'bg-orange-100 dark:bg-orange-500/20 border-orange-400 dark:border-orange-500/50 ring-2 ring-orange-200 dark:ring-orange-500/30',
        iconColor: 'text-orange-600 dark:text-orange-400',
        iconBg: 'bg-white dark:bg-[#222529]',
        iconBgInactive: 'bg-white/60 dark:bg-[#222529]/60',
        checkBg: 'bg-white dark:bg-[#222529]',
        bannerBg: 'bg-orange-50 dark:bg-orange-500/10 border-b border-orange-100 dark:border-orange-500/20',
    },
};

// ----------------------------------------------------------------------
// NovoChamado (Drawer)
// ----------------------------------------------------------------------
export default function NovoChamado({
    user,
    config,
    listaInputsPrioridade,
    isOpen,
    onClose,
    onCriarChamado,
    selectAssunto,
    tipoLocal,
    setorOuUsuario,
    usuarioEscolhido,
    setorEscolhido,
    filialEscolhido,
    estimativa,
    assunto,
    files,
    descricao,
    preview,
    isFile,
    emCopia,
    changeEmCopia,
    changeDescricao,
    changeAssunto,
    changeIsFile,
    changeEstimativa,
    changeTipoLocal,
    changeSetorOuUsuario,
    changeUsuarioEscolhido,
    changeFilialEscolhido,
    changeSetorEscolhido,
    readOnlyDescricao,
    handleDropMultiFile,
    handleRemove,
    modalAssunto,
    closeModalAssunto,
    openModalAssunto,
    loading,
    dataConclusao,
    changeDataConclusao,
    prioridade,
    changePrioridade,
    caixaEntrada,
    changeCaixaEntrada,
    respondenteName,
    changeRespondenteName,
    respondenteEmail,
    changeRespondenteEmail,
    checklist,
    addChecklistItem,
    toggleChecklistItem,
    removeChecklistItem,
}) {
    const [tableData, setTableData] = useState([]);
    const [loadingEnviarArquivos, setLoadingEnviarArquivos] = useState(false);
    const [pesquisar, setPesquisar] = useState('');
    const [newChecklistItem, setNewChecklistItem] = useState('');
    const [listaUsuarios, setListaUsuarios] = useState([]);
    const [listaSetores, setListaSetores] = useState([]);
    const [listaFiliais, setListaFiliais] = useState([]);
    const [listaCaixas, setListaCaixas] = useState([]);

    const buscarUsuarios = async () => {
        try {
            const res = await getActionListaUsuariosByClienteId(user.clienteId);
            setListaUsuarios(res.data.usuarios);
        } catch (error) {
            console.log(error);
        }
    };

    const buscarSetores = async () => {
        try {
            const res = await getActionListaSetoresByClienteId(user.clienteId);
            setListaSetores(res.data.setores);
        } catch (error) { }
    };

    const buscarCaixasEntrada = async () => {
        try {
            const response = await actionListaCaixasEntrada(`?cliente_id=${user.clienteId}`);
            setListaCaixas(response.data.data);
        } catch (error) { }
    };

    useEffect(() => {
        buscarUsuarios();
        buscarSetores();
        buscarCaixasEntrada();
    }, []);

    const dataFiltered = applySortFilter({ tableData, pesquisar });

    const handleIsFileChange = (checked) => {
        changeIsFile({ target: { checked } });
    };

    const handleAssuntoChange = (e) => {
        changeAssunto(e);
    };

    const handleSetorOuUsuarioChange = (value) => {
        changeSetorOuUsuario({ target: { value } });
    };

    const handleTipoChange = (tipo) => {
        changeTipoLocal({ target: { value: tipo } });
    };

    const handleAddChecklistItem = () => {
        if (!newChecklistItem.trim()) return;
        addChecklistItem(newChecklistItem);
        setNewChecklistItem('');
    };

    const handleChecklistKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddChecklistItem();
        }
    };

    const checklistProgress = checklist && checklist.length > 0
        ? Math.round((checklist.filter(i => i.checked).length / checklist.length) * 100)
        : 0;

    // Dados do resumo
    const tipoCfg = TIPO_CONFIG[tipoLocal] || TIPO_CONFIG.chamado_interno;
    const destinatarioLabel = tipoLocal === 'chamado_interno'
        ? setorOuUsuario === 'usuario' && usuarioEscolhido
            ? usuarioEscolhido.nomeFantasia
            : setorEscolhido
                ? setorEscolhido.nome_setor
                : null
        : filialEscolhido
            ? filialEscolhido.nome_fantasia
            : null;

    if (!isOpen) return null;

    return (
        <>
            {/* Modal de assuntos pre-selecionados */}
            <Dialog open={modalAssunto} onOpenChange={closeModalAssunto}>
                <DialogContent className="max-w-4xl p-0 gap-0 rounded-xl overflow-hidden">
                    <DialogHeader className="p-6 pb-2">
                        <DialogTitle className="text-xl font-bold text-gray-900 dark:text-[#D1D2D3]">Escolha um assunto pre-selecionado</DialogTitle>
                    </DialogHeader>
                    <div className="px-6 py-4">
                        <Input
                            placeholder="Pesquisar Assunto"
                            onChange={(e) => setPesquisar(e.target.value)}
                            value={pesquisar}
                            className="w-full rounded-xl bg-gray-50 dark:bg-[#383838] border-gray-200 dark:border-[#565856] focus:border-[#7C3AED] focus:ring-0 dark:text-[#D1D2D3]"
                        />
                        <ScrollArea className="mt-4 h-[340px]">
                            <div className="space-y-1">
                                {dataFiltered.map((list, index) => (
                                    <AssuntosCard key={index} selectAssunto={() => selectAssunto(list)} list={list} />
                                ))}
                            </div>
                        </ScrollArea>
                    </div>
                    <DialogFooter className="p-6 pt-2 bg-white dark:bg-[#222529]">
                        <Button variant="ghost" onClick={closeModalAssunto} className="text-gray-600 dark:text-[#ABABAD] hover:bg-gray-50 dark:hover:bg-[#383838]">
                            Fechar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Drawer overlay */}
            <div className="fixed inset-0 z-[9999] flex justify-end">
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200"
                    onClick={onClose}
                />

                {/* Panel */}
                <div className="relative w-[90%] max-w-5xl bg-white dark:bg-[#222529] flex flex-col shadow-2xl animate-in slide-in-from-right duration-300">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-100 dark:border-[#383838] flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center">
                                <Iconify icon="eva:plus-outline" width={22} className="text-violet-600 dark:text-violet-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-900 dark:text-[#D1D2D3]">Criar Novo Chamado</h2>
                                <p className="text-xs text-gray-500 dark:text-[#ABABAD]">Preencha as informacoes para abrir um novo ticket</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-xl text-gray-400 dark:text-[#ABABAD] hover:bg-gray-100 dark:hover:bg-[#383838] hover:text-gray-600 dark:hover:text-[#D1D2D3] transition-colors"
                        >
                            <Iconify icon="eva:close-outline" width={20} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex overflow-hidden">
                        {config ? (
                            <>
                                {/* Left Column - Formulario */}
                                <div className="w-[62%] border-r border-gray-100 dark:border-[#383838] overflow-y-auto">
                                    <div className="p-5 flex flex-col gap-5">

                                        {/* Tipo do Chamado — inline */}
                                        <div className="flex items-center gap-3">
                                            <div className="flex-shrink-0">
                                                <FieldLabel required tooltip="Define se o chamado é entre usuários da plataforma (interno) ou de público externo">
                                                    Tipo
                                                </FieldLabel>
                                            </div>
                                            <div className="flex gap-2 flex-1">
                                                {Object.entries(TIPO_CONFIG).map(([key, cfg]) => (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        onClick={() => handleTipoChange(key)}
                                                        className={cn(
                                                            'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 text-left flex-1',
                                                            tipoLocal === key
                                                                ? 'border-[#7C3AED] bg-violet-50/50 dark:bg-violet-500/10 ring-1 ring-[#7C3AED]/20'
                                                                : 'border-gray-200 dark:border-[#383838] hover:border-gray-300 dark:hover:border-[#565856]'
                                                        )}
                                                    >
                                                        <Iconify icon={cfg.icon} width={16} className={tipoLocal === key ? 'text-[#7C3AED]' : 'text-gray-400'} />
                                                        <span className={cn('text-[13px] font-medium', tipoLocal === key ? 'text-[#7C3AED]' : 'text-gray-600 dark:text-gray-400')}>{cfg.label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Assunto */}
                                        <div className="space-y-1.5">
                                            <FieldLabel required tooltip="Título resumido do chamado. Será exibido nas listagens e notificações">
                                                Assunto
                                            </FieldLabel>
                                            <Input
                                                value={assunto}
                                                onChange={handleAssuntoChange}
                                                placeholder="Descreva brevemente o chamado"
                                                className="rounded-lg border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] dark:text-[#D1D2D3] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
                                            />
                                        </div>

                                        {/* Descricao */}
                                        <div className="space-y-1.5">
                                            <FieldLabel tooltip="Detalhamento do chamado. Inclua todas as informações relevantes para a resolução">
                                                {config?.chamados?.novo_chamado?.titulo_descricao || 'Descricao'}
                                            </FieldLabel>
                                            <div className="rounded-lg border border-gray-200 dark:border-[#383838] overflow-hidden focus-within:ring-1 focus-within:ring-[#7C3AED]/30 focus-within:border-[#7C3AED]">
                                                <Editor value={descricao} onChange={changeDescricao} readOnly={readOnlyDescricao} />
                                            </div>
                                        </div>

                                        {/* Prioridade (interno: sempre visível / externo: só após selecionar caixa) */}
                                        {tipoLocal !== 'chamado_externo' && listaInputsPrioridade && listaInputsPrioridade.length > 0 && (
                                            <div className="space-y-1.5">
                                                <FieldLabel required tooltip="Nível de urgência do chamado. Afeta os tempos de aceite e conclusão (SLA)">
                                                    Prioridade
                                                </FieldLabel>
                                                <Combobox
                                                    options={listaInputsPrioridade.filter(p => p.ativo !== false)}
                                                    value={listaInputsPrioridade.find(p => p._id === prioridade) || null}
                                                    onChange={(option) => changePrioridade(option ? option._id : '')}
                                                    getOptionLabel={(option) => option.titulo || ''}
                                                    renderOption={(option) => (
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: option.cor }} />
                                                            <span>{option.titulo}</span>
                                                        </div>
                                                    )}
                                                    renderValue={(option) => (
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: option.cor }} />
                                                            <span>{option.titulo}</span>
                                                        </div>
                                                    )}
                                                    placeholder="Selecione a prioridade..."
                                                    className="rounded-lg"
                                                />
                                            </div>
                                        )}

                                        {/* Prazo + Direcionamento — lado a lado */}
                                        <div className="grid grid-cols-2 gap-4">
                                            {/* Prazo */}
                                            <div className="space-y-1.5">
                                                <FieldLabel tooltip="Data e hora limite para conclusão do chamado">
                                                    Prazo
                                                </FieldLabel>
                                                <DateTimePicker value={dataConclusao} onChange={changeDataConclusao} />
                                            </div>

                                            {/* Direcionamento tipo */}
                                            <div className="space-y-1.5">
                                                <FieldLabel required tooltip="Define quem receberá o chamado. Setor distribui entre membros, Usuário envia para alguém específico">
                                                    Direcionar para
                                                </FieldLabel>
                                                <div className="flex gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSetorOuUsuarioChange('setor')}
                                                        className={cn(
                                                            'flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border text-[12px] font-medium transition-all',
                                                            setorOuUsuario === 'setor'
                                                                ? 'border-[#7C3AED] bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED]'
                                                                : 'border-gray-200 dark:border-[#383838] text-gray-500 hover:border-gray-300'
                                                        )}
                                                    >
                                                        Setor
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSetorOuUsuarioChange('usuario')}
                                                        className={cn(
                                                            'flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border text-[12px] font-medium transition-all',
                                                            setorOuUsuario === 'usuario'
                                                                ? 'border-[#7C3AED] bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED]'
                                                                : 'border-gray-200 dark:border-[#383838] text-gray-500 hover:border-gray-300'
                                                        )}
                                                    >
                                                        Usuário
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Campos para chamado externo */}
                                        {tipoLocal === 'chamado_externo' && (
                                            <div className="space-y-4 animate-in fade-in duration-200">
                                                <div className="space-y-1.5">
                                                    <FieldLabel required tooltip="Caixa de entrada que receberá o chamado externo. Define o formulário e fluxo do chamado">
                                                        Caixa de Entrada
                                                    </FieldLabel>
                                                    <Combobox
                                                        options={listaCaixas}
                                                        value={caixaEntrada}
                                                        onChange={changeCaixaEntrada}
                                                        getOptionLabel={(option) => option.nome || option.name || ''}
                                                        placeholder="Selecione a caixa de entrada..."
                                                        className="rounded-lg"
                                                    />
                                                </div>
                                                {/* Prioridade por caixa (só aparece após selecionar caixa) */}
                                                {caixaEntrada && listaInputsPrioridade && listaInputsPrioridade.length > 0 && (
                                                    <div className="space-y-1.5 animate-in fade-in duration-200">
                                                        <FieldLabel required tooltip="Nível de urgência do chamado. Afeta os tempos de aceite e conclusão (SLA)">
                                                            Prioridade
                                                        </FieldLabel>
                                                        <Combobox
                                                            options={listaInputsPrioridade.filter(p => p.ativo !== false)}
                                                            value={listaInputsPrioridade.find(p => p._id === prioridade) || null}
                                                            onChange={(option) => changePrioridade(option ? option._id : '')}
                                                            getOptionLabel={(option) => option.titulo || ''}
                                                            renderOption={(option) => (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: option.cor }} />
                                                                    <span>{option.titulo}</span>
                                                                </div>
                                                            )}
                                                            renderValue={(option) => (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: option.cor }} />
                                                                    <span>{option.titulo}</span>
                                                                </div>
                                                            )}
                                                            placeholder="Selecione a prioridade..."
                                                            className="rounded-lg"
                                                        />
                                                    </div>
                                                )}
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-1.5">
                                                        <FieldLabel required tooltip="Nome completo da pessoa externa que está solicitando o chamado">
                                                            Nome do Respondente
                                                        </FieldLabel>
                                                        <Input
                                                            value={respondenteName}
                                                            onChange={changeRespondenteName}
                                                            placeholder="Nome completo"
                                                            className="rounded-lg border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] dark:text-[#D1D2D3] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
                                                        />
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <FieldLabel required tooltip="E-mail do respondente externo. Será usado para comunicação e acesso ao portal">
                                                            Email do Respondente
                                                        </FieldLabel>
                                                        <Input
                                                            type="email"
                                                            value={respondenteEmail}
                                                            onChange={changeRespondenteEmail}
                                                            placeholder="email@exemplo.com"
                                                            className="rounded-lg border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] dark:text-[#D1D2D3] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Setor/Usuario select */}
                                        {setorOuUsuario && (
                                            <div className="space-y-1.5 animate-in fade-in duration-200">
                                                <FieldLabel
                                                    required
                                                    tooltip={setorOuUsuario === 'setor'
                                                        ? 'Setor que receberá o chamado. O chamado será distribuído entre os membros do setor'
                                                        : 'Usuário específico que receberá e será responsável pelo chamado'
                                                    }
                                                >
                                                    {setorOuUsuario === 'setor' ? 'Escolha o Setor' : 'Escolha o Usuário'}
                                                </FieldLabel>
                                                {setorOuUsuario === 'setor' ? (
                                                    <Combobox
                                                        options={listaSetores}
                                                        value={setorEscolhido}
                                                        onChange={changeSetorEscolhido}
                                                        getOptionLabel={(option) => option.nome_setor}
                                                        placeholder="Selecione o setor..."
                                                        className="rounded-lg"
                                                    />
                                                ) : (
                                                    <Combobox
                                                        options={listaUsuarios}
                                                        value={usuarioEscolhido}
                                                        onChange={changeUsuarioEscolhido}
                                                        getOptionLabel={(option) =>
                                                            `${option.nomeFantasia} ${option.setor ? '(' + option.nome_setor + ')' : ''}`
                                                        }
                                                        placeholder="Selecione o usuario..."
                                                        className="rounded-lg"
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {/* Em Copia */}
                                        {config?.chamados?.novo_chamado?.status_em_copia && (
                                            <div className="space-y-1.5">
                                                <FieldLabel tooltip="Usuários que receberão notificações sobre este chamado, sem serem os responsáveis diretos">
                                                    {config.chamados.novo_chamado.titulo_em_copia}{' '}
                                                    <span className="text-[10px] normal-case opacity-70">(opcional)</span>
                                                </FieldLabel>
                                                <Combobox
                                                    multiple
                                                    options={listaUsuarios}
                                                    value={emCopia || []}
                                                    onChange={changeEmCopia}
                                                    getOptionLabel={(option) => option.nomeFantasia + ' (' + option.nome_setor + ')'}
                                                    placeholder="Selecionar usuarios"
                                                    className="rounded-lg"
                                                />
                                            </div>
                                        )}

                                        {/* Secao: Anexos */}
                                        {config?.chamados?.novo_chamado?.status_anexar_arquivos && (
                                            <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-5">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-8 h-8 rounded-lg bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
                                                            <Iconify icon="eva:attach-outline" width={18} className="text-violet-600 dark:text-violet-400" />
                                                        </div>
                                                        <span className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3]">Anexos</span>
                                                    </div>
                                                    <Switch
                                                        checked={isFile}
                                                        onCheckedChange={handleIsFileChange}
                                                        id="anexar-arquivos"
                                                        className="data-[state=checked]:bg-[#7C3AED]"
                                                    />
                                                </div>
                                                {isFile && (
                                                    <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                                        <UploadChamados
                                                            loadingEnviarArquivos={loadingEnviarArquivos}
                                                            showPreview={preview}
                                                            files={files}
                                                            onDrop={handleDropMultiFile}
                                                            onRemove={handleRemove}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Secao: Checklist */}
                                        <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-4">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-8 h-8 rounded-lg bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
                                                        <Iconify icon="mdi:checkbox-marked-outline" width={18} className="text-violet-600 dark:text-violet-400" />
                                                    </div>
                                                    <span className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3]">Checklist</span>
                                                </div>
                                                {checklist && checklist.length > 0 && (
                                                    <span className="text-[10px] font-medium text-gray-400 dark:text-[#ABABAD]">
                                                        {checklist.filter(i => i.checked).length}/{checklist.length}
                                                    </span>
                                                )}
                                            </div>

                                            {checklist && checklist.length > 0 && (
                                                <div className="w-full h-1.5 bg-gray-100 dark:bg-[#383838] rounded-full mb-3 overflow-hidden">
                                                    <div
                                                        className="h-full bg-[#7C3AED] rounded-full transition-all duration-300"
                                                        style={{ width: `${checklistProgress}%` }}
                                                    />
                                                </div>
                                            )}

                                            <div className="space-y-1">
                                                {checklist && checklist.map((item) => (
                                                    <div
                                                        key={item.id}
                                                        className="flex items-center gap-2 group p-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1A1D21] transition-colors"
                                                    >
                                                        <Checkbox
                                                            checked={item.checked}
                                                            onCheckedChange={() => toggleChecklistItem(item.id)}
                                                            className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                                                        />
                                                        <span className={`flex-1 text-sm ${item.checked ? 'line-through text-gray-400 dark:text-[#ABABAD]/50' : 'text-gray-700 dark:text-[#D1D2D3]'}`}>
                                                            {item.text}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeChecklistItem(item.id)}
                                                            className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-red-500 transition-all"
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="flex items-center gap-2 mt-2">
                                                <Input
                                                    value={newChecklistItem}
                                                    onChange={e => setNewChecklistItem(e.target.value)}
                                                    onKeyDown={handleChecklistKeyDown}
                                                    placeholder="Adicionar item..."
                                                    className="flex-1 h-8 text-sm border-gray-200 dark:border-[#383838] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-lg dark:bg-[#1A1D21] dark:text-[#D1D2D3]"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleAddChecklistItem}
                                                    disabled={!newChecklistItem.trim()}
                                                    className="flex items-center gap-1 text-xs text-[#7C3AED] font-medium hover:underline disabled:opacity-40 disabled:no-underline"
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column - Resumo */}
                                <div className="w-[38%] bg-gradient-to-b from-slate-50 to-slate-100 dark:from-[#1A1D21] dark:to-[#1A1D21] overflow-y-auto">
                                    <div className="p-6 flex flex-col gap-5">
                                        <div className="flex items-center gap-2.5 mb-1">
                                            <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-[#383838] flex items-center justify-center">
                                                <Iconify icon="eva:eye-outline" width={18} className="text-slate-600 dark:text-[#ABABAD]" />
                                            </div>
                                            <span className="text-sm font-bold text-gray-700 dark:text-[#D1D2D3]">Resumo do Chamado</span>
                                        </div>

                                        {/* Preview Card */}
                                        <div className="bg-white dark:bg-[#222529] rounded-2xl border border-gray-200 dark:border-[#383838] shadow-sm overflow-hidden">
                                            {/* Tipo banner */}
                                            <div className={cn(
                                                'px-4 py-3 flex items-center gap-2.5',
                                                tipoCfg.bannerBg
                                            )}>
                                                <Iconify
                                                    icon={tipoCfg.icon}
                                                    width={18}
                                                    className={tipoCfg.iconColor}
                                                />
                                                <span className={cn('text-xs font-bold uppercase tracking-wider', tipoCfg.iconColor)}>
                                                    Chamado {tipoCfg.label}
                                                </span>
                                            </div>

                                            <div className="p-4 flex flex-col gap-3.5">
                                                {/* Assunto */}
                                                <div>
                                                    <p className="text-[10px] font-semibold text-gray-400 dark:text-[#ABABAD] uppercase tracking-wider mb-1">Assunto</p>
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3] leading-snug">
                                                        {assunto || <span className="text-gray-300 dark:text-[#565856] italic font-normal">Nao informado</span>}
                                                    </p>
                                                </div>

                                                <Separator className="bg-gray-100 dark:bg-[#383838]" />

                                                {/* Direcionado para */}
                                                <div>
                                                    <p className="text-[10px] font-semibold text-gray-400 dark:text-[#ABABAD] uppercase tracking-wider mb-1">Direcionado para</p>
                                                    {destinatarioLabel ? (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 rounded-lg bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center">
                                                                <Iconify
                                                                    icon={setorOuUsuario === 'usuario' ? 'eva:person-outline' : tipoLocal === 'chamado_externo' ? 'mdi:store-outline' : 'mdi:account-group-outline'}
                                                                    width={14}
                                                                    className="text-violet-600 dark:text-violet-400"
                                                                />
                                                            </div>
                                                            <span className="text-sm text-gray-700 dark:text-[#D1D2D3] font-medium">{destinatarioLabel}</span>
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-300 dark:text-[#565856] italic">Nao selecionado</p>
                                                    )}
                                                </div>

                                                <Separator className="bg-gray-100 dark:bg-[#383838]" />

                                                {/* Prazo */}
                                                <div>
                                                    <p className="text-[10px] font-semibold text-gray-400 dark:text-[#ABABAD] uppercase tracking-wider mb-1">Prazo</p>
                                                    {dataConclusao ? (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                                                                <Iconify icon="mdi:clock-outline" width={14} className="text-amber-600 dark:text-amber-400" />
                                                            </div>
                                                            <span className="text-sm text-gray-700 dark:text-[#D1D2D3] font-medium">
                                                                {format(new Date(dataConclusao), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-300 dark:text-[#565856] italic">Nao definido</p>
                                                    )}
                                                </div>

                                                {/* Prioridade */}
                                                <Separator className="bg-gray-100 dark:bg-[#383838]" />
                                                <div>
                                                    <p className="text-[10px] font-semibold text-gray-400 dark:text-[#ABABAD] uppercase tracking-wider mb-1">Prioridade</p>
                                                    {prioridade && listaInputsPrioridade.find(p => p._id === prioridade) ? (
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                                                style={{ backgroundColor: listaInputsPrioridade.find(p => p._id === prioridade).cor }}
                                                            />
                                                            <span className="text-sm text-gray-700 dark:text-[#D1D2D3] font-medium">
                                                                {listaInputsPrioridade.find(p => p._id === prioridade).titulo}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-300 dark:text-[#565856] italic">Nao selecionada</p>
                                                    )}
                                                </div>

                                                {/* Caixa de Entrada + Respondente (externo) */}
                                                {tipoLocal === 'chamado_externo' && caixaEntrada && (
                                                    <>
                                                        <Separator className="bg-gray-100 dark:bg-[#383838]" />
                                                        <div>
                                                            <p className="text-[10px] font-semibold text-gray-400 dark:text-[#ABABAD] uppercase tracking-wider mb-1">Caixa de Entrada</p>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 rounded-lg bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center">
                                                                    <Iconify icon="mdi:inbox" width={14} className="text-orange-600 dark:text-orange-400" />
                                                                </div>
                                                                <span className="text-sm text-gray-700 dark:text-[#D1D2D3] font-medium">{caixaEntrada.nome || caixaEntrada.name}</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {tipoLocal === 'chamado_externo' && respondenteEmail && (
                                                    <>
                                                        <Separator className="bg-gray-100 dark:bg-[#383838]" />
                                                        <div>
                                                            <p className="text-[10px] font-semibold text-gray-400 dark:text-[#ABABAD] uppercase tracking-wider mb-1">Respondente</p>
                                                            <div className="flex flex-col gap-0.5">
                                                                {respondenteName && <span className="text-sm text-gray-700 dark:text-[#D1D2D3] font-medium">{respondenteName}</span>}
                                                                <span className="text-xs text-gray-500 dark:text-[#ABABAD]">{respondenteEmail}</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                                {/* Em Copia */}
                                                {emCopia && emCopia.length > 0 && (
                                                    <>
                                                        <Separator className="bg-gray-100 dark:bg-[#383838]" />
                                                        <div>
                                                            <p className="text-[10px] font-semibold text-gray-400 dark:text-[#ABABAD] uppercase tracking-wider mb-1.5">Em Copia</p>
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {emCopia.map((u, i) => (
                                                                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 border border-violet-200 dark:border-violet-500/30">
                                                                        {u.nomeFantasia}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                                {/* Anexos */}
                                                {files && files.length > 0 && (
                                                    <>
                                                        <Separator className="bg-gray-100 dark:bg-[#383838]" />
                                                        <div>
                                                            <p className="text-[10px] font-semibold text-gray-400 dark:text-[#ABABAD] uppercase tracking-wider mb-1.5">Anexos</p>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 rounded-lg bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                                                                    <Iconify icon="eva:file-outline" width={14} className="text-emerald-600 dark:text-emerald-400" />
                                                                </div>
                                                                <span className="text-sm text-gray-700 dark:text-[#D1D2D3] font-medium">
                                                                    {files.length} arquivo{files.length > 1 ? 's' : ''}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                                {/* Checklist */}
                                                {checklist && checklist.length > 0 && (
                                                    <>
                                                        <Separator className="bg-gray-100 dark:bg-[#383838]" />
                                                        <div>
                                                            <p className="text-[10px] font-semibold text-gray-400 dark:text-[#ABABAD] uppercase tracking-wider mb-1.5">Checklist</p>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 rounded-lg bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center">
                                                                    <Iconify icon="mdi:checkbox-marked-outline" width={14} className="text-violet-600 dark:text-violet-400" />
                                                                </div>
                                                                <span className="text-sm text-gray-700 dark:text-[#D1D2D3] font-medium">
                                                                    {checklist.filter(i => i.checked).length}/{checklist.length} concluido{checklist.length > 1 ? 's' : ''}
                                                                </span>
                                                            </div>
                                                            <div className="w-full h-1 bg-gray-100 dark:bg-[#383838] rounded-full mt-2 overflow-hidden">
                                                                <div
                                                                    className="h-full rounded-full transition-all duration-300"
                                                                    style={{
                                                                        width: `${checklistProgress}%`,
                                                                        backgroundColor: checklistProgress === 100 ? '#22c55e' : '#7C3AED',
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Dicas */}
                                        <div className="bg-violet-50/60 dark:bg-violet-500/5 rounded-xl border border-violet-100 dark:border-violet-500/20 p-4">
                                            <div className="flex items-start gap-2.5">
                                                <Iconify icon="mdi:lightbulb-outline" width={18} className="text-violet-500 dark:text-violet-400 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <p className="text-xs font-semibold text-violet-700 dark:text-violet-400 mb-1">Dica</p>
                                                    <p className="text-[11px] text-violet-600/80 dark:text-violet-300/60 leading-relaxed">
                                                        {tipoLocal === 'chamado_externo'
                                                            ? 'Chamados externos sao direcionados para publico de fora da plataforma. Selecione a franquia correspondente.'
                                                            : 'Direcionar para um setor distribui o chamado entre os membros disponíveis. Para um caso especifico, escolha um usuario.'
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center py-10 text-center">
                                <p className="text-gray-500 dark:text-[#ABABAD]">Nao foi possivel carregar as configuracoes.</p>
                                <Button variant="link" onClick={() => window.location.reload()}>
                                    Recarregar pagina
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-100 dark:border-[#383838] bg-gray-50/80 dark:bg-[#1A1D21]/80 px-6 py-4 flex items-center justify-end gap-3">
                        <Button
                            variant="ghost"
                            onClick={onClose}
                            className="rounded-xl text-gray-600 dark:text-[#ABABAD] hover:bg-gray-100 dark:hover:bg-[#383838]"
                        >
                            Cancelar
                        </Button>
                        <LoadingButton
                            disabled={
                                loading
                                || !assunto
                                || !prioridade
                                || (setorOuUsuario === 'setor' && !setorEscolhido)
                                || (setorOuUsuario === 'usuario' && !usuarioEscolhido)
                                || (tipoLocal === 'chamado_externo' && (!caixaEntrada || !respondenteEmail || !respondenteName))
                            }
                            loading={loading}
                            onClick={onCriarChamado}
                            className="rounded-xl bg-[#7C3AED] hover:bg-[#6d28d9] text-white shadow-sm shadow-[#7C3AED]/20 px-6 active:transform active:scale-95 transition-all"
                        >
                            {(!assunto || !prioridade || (setorOuUsuario === 'setor' && !setorEscolhido) || (setorOuUsuario === 'usuario' && !usuarioEscolhido) || (tipoLocal === 'chamado_externo' && (!caixaEntrada || !respondenteEmail || !respondenteName))) ? (
                                <>
                                    <Iconify icon="mdi:alert-circle-outline" width={18} className="mr-1.5" />
                                    Preencha os campos
                                </>
                            ) : (
                                <>
                                    <Iconify icon="eva:plus-outline" width={18} className="mr-1.5" />
                                    Criar Chamado
                                </>
                            )}
                        </LoadingButton>
                    </div>
                </div>
            </div>
        </>
    );
}

// ----------------------------------------------------------------------
// AssuntosCard
// ----------------------------------------------------------------------
AssuntosCard.propTypes = {
    list: PropTypes.object,
    selectAssunto: PropTypes.func,
};

function AssuntosCard({ list, selectAssunto }) {
    const { assunto } = list;
    return (
        <button
            onClick={selectAssunto}
            className="w-full text-left py-3 px-4 text-lg font-bold hover:bg-accent rounded-md transition-colors"
        >
            {assunto}
        </button>
    );
}

function applySortFilter({ tableData, pesquisar }) {
    if (pesquisar) {
        tableData = tableData.filter((item) => item.assunto.toLowerCase().indexOf(pesquisar.toLowerCase()) !== -1);
    }
    return tableData;
}
